import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertBanner from "../shared/AlertBanner";
import RewardsBanner, { type RewardsBannerVariant } from "../shared/RewardsBanner";
import RewardsSignUpSection from "../shared/RewardsSignUpSection";
import RewardsSignInSection from "../shared/RewardsSignInSection";
import BookingConfirmedHeader from "../confirmation/BookingConfirmedHeader";
import BookingDetailsCard from "../confirmation/BookingDetailsCard";
import BookingActionSummaryCard from "../confirmation/BookingActionSummaryCard";
import LinkedBookingsDetails, { type LinkedBookingSummary } from "../confirmation/LinkedBookingsDetails";
import FeatureSentiment from "../confirmation/FeatureSentiment";
import { formatBookingDate, formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import { isRewardsEligible } from "../../utils/rewardsEligibility";
import { useConfirmationRewards } from "../hooks/useConfirmationRewards";
import analytics from "../../analytics";
import ReturnUrlRedirect from "../shared/ReturnUrlRedirect";
import { returnRedirectFromSubmit } from "../../utils/returnUrlRedirect";
import type { BookingConfirmationData, PreorderSelections } from "./BookingForm";
import type { Rewards } from "../../../common/jsonRpcTypes";

interface BookingConfirmationProps {
  readonly confirmationData: BookingConfirmationData | null;
  readonly returnUrl?: string;
  readonly returnMethod?: string;
  readonly isRedirect: boolean;
  /** Booking duration in minutes, sent to the partner `return_url` as legacy DMN did. */
  readonly duration?: number | null;
  /** Rewards data returned by the /createBooking API (points earned for this booking). */
  readonly rewards?: Rewards | null;
  /** Booking source — rewards and sign-up are hidden for ineligible sources. */
  readonly source?: string;
  /** Affiliate id, when present. Affiliate bookings never earn reward points. */
  readonly affiliateId?: string | null;
  /** Newsletter signup preference from the customer details form. */
  readonly newsletterSignup?: boolean;
  /** Whether the user was logged in (pre-authenticated or runtime login) during the booking flow. */
  readonly isLoggedIn?: boolean;
  /** The signed-in guest's reward points balance, shown alongside the banner. */
  readonly pointsBalance?: number;
  /** Whether the booking was submitted as an enquiry. */
  readonly isEnquiry?: boolean;
  /** Special request / notes entered by the customer. */
  readonly specialRequest?: string;
  /** Label of the requested package, if one was selected. */
  readonly requestedPackageLabel?: string;
  /** Title of the offer the guest added to the booking, if they chose one. */
  readonly selectedOfferTitle?: string;
  /** Preorder selections made during the booking flow. */
  readonly preorderSelections?: PreorderSelections | null;
  /** Deposit kind required for this booking type, used to describe the payment outcome. */
  readonly depositType?: "payment" | "authenticate" | "auth";
  /**
   * The pending booking the payment was initiated against, reported on `purchase`
   * as `payment_journey_id` so the conversion can be matched to its initiation
   * even though the confirmed booking has a different id.
   */
  readonly paymentJourneyId?: string;
}

/**
 * BookingConfirmation — Step 7 of the booking journey.
 *
 * Shows the booking confirmation or acknowledgement after successful
 * submission. Displays booking reference, venue name, date/time,
 * party size, booking type, deposit paid, and linked bookings.
 *
 * When a `returnUrl` is provided, the page would redirect after
 * a 3-second delay using either GET or POST method.
 *
 * Business logic (redirect, form POST) is intentionally omitted.
 */
const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  confirmationData,
  returnUrl,
  returnMethod,
  isRedirect,
  duration,
  rewards,
  source,
  affiliateId,
  newsletterSignup,
  isLoggedIn,
  pointsBalance,
  isEnquiry,
  specialRequest,
  requestedPackageLabel,
  selectedOfferTitle,
  preorderSelections,
  depositType,
  paymentJourneyId,
}) => {
  const { t } = useTranslation();
  const timeLocale = useWidgetLocale(confirmationData?.venue.country_code);
  // Resolves once conversion beacons have been dispatched, so a partner
  // redirect can be held open until the events have left the browser.
  const purchaseTracked = useRef<Promise<void>>(Promise.resolve());
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpBalance, setSignUpBalance] = useState<number | undefined>(undefined);
  /**
   * Whether the guest signed in to an existing account here, tracked separately
   * from the balance because DMN may return no balance and the form must still
   * be replaced by the signed-in message.
   */
  const [signedIn, setSignedIn] = useState(false);
  const [signedInBalance, setSignedInBalance] = useState<number | undefined>(undefined);

  const handleSignInSuccess = useCallback((pointsBalance?: number) => {
    setSignedIn(true);
    setSignedInBalance(pointsBalance);
  }, []);

  // `purchase` — a booking happened. De-duplicated per booking inside the service.
  useEffect(() => {
    if (!confirmationData) return;

    const { booking, bookingStatus, venue } = confirmationData;
    const paid = (booking.deposits ?? []).reduce((sum, deposit) => sum + deposit.amount, 0);

    purchaseTracked.current = analytics.purchase({
      id: booking._id,
      journeyId: paymentJourneyId,
      typeId: booking.type.id,
      typeName: booking.type.name,
      numPeople: booking.num_people,
      value: paid,
      currency: venue.currency,
      status: bookingStatus,
    });
  }, [confirmationData, paymentJourneyId]);

  const returnRedirect = useMemo(
    () => (confirmationData ? returnRedirectFromSubmit(confirmationData, duration) : null),
    [confirmationData, duration],
  );

  const rewardsEligible = isRewardsEligible({
    rewards,
    source,
    affiliateId,
    bookingTypeName: confirmationData?.booking?.type?.name,
  });

  // When the guest did not sign in during the flow, their email is still
  // checked for an existing DesignMyNight account — Collins attaches points by
  // email, so an existing account already earns them and must not be offered a
  // sign-up that would fail with "email already registered". Mirrors /confirm.
  const { rewardsUrl, hasAccount, canSignUp } = useConfirmationRewards({
    email: isLoggedIn ? undefined : (confirmationData?.booking?.email ?? undefined),
    enabled: rewardsEligible,
  });

  // A guest with no account has no `User` for Collins to attach points to, so
  // the copy must offer sign-up rather than promise points outright.
  const rewardsVariant: RewardsBannerVariant = (() => {
    if (signUpSuccess) return "justSignedUp";
    return isLoggedIn || hasAccount === true ? "hasAccount" : "signedOut";
  })();

  // Offered only to a guest we know has no account yet. Redirecting bookings
  // are excluded because the page is about to navigate away.
  const showSignUpSection = rewardsEligible && !signUpSuccess && !isLoggedIn && canSignUp && !isRedirect;

  // A guest whose email already has an account is offered sign-in instead: they
  // already earn the points, but signing in is what lets them see the balance
  // and opens the rewards link authenticated.
  const showSignInSection = rewardsEligible && !signedIn && !isLoggedIn && hasAccount === true && !isRedirect;

  if (!confirmationData) {
    return (
      <div className="gcw:flex gcw:justify-center gcw:py-bs-5">
        <div className="loading-spinner" />
      </div>
    );
  }

  const { booking, additionalBookings, venue, bookingStatus } = confirmationData;
  const deposits = booking.deposits ?? [];
  const hasDeposits = deposits.length > 0;
  // Collins returns the full booking document for each linked booking, so map it
  // onto the display shape shared with the /confirm page.
  const linkedBookings: LinkedBookingSummary[] = additionalBookings.map((linked) => ({
    id: linked._id,
    reference: linked.reference,
    typeName: linked.type?.name,
    date: linked.date,
    time: linked.time,
    partySize: linked.num_people ?? undefined,
  }));
  const hasPreorders = !!preorderSelections && preorderSelections.items.length > 0;

  // Describe what the guest did (pre-order / payment / authenticate) for the status card.
  const isAuthenticate = depositType === "auth" || depositType === "authenticate";
  const didAuthenticate = hasDeposits && isAuthenticate;
  const didPay = hasDeposits && !isAuthenticate;
  const paidAmount = deposits.reduce((sum, deposit) => sum + deposit.amount, 0);

  // Two outcomes only: an enquiry awaiting confirmation, or a confirmed booking.
  // Collins has the final say, so a booking it reports as unconfirmed is shown
  // as an enquiry even when the guest was not routed through the enquiry flow.
  const headerMode: "enquiry" | "bookingOnly" = isEnquiry || bookingStatus === "received" ? "enquiry" : "bookingOnly";
  const customerName = [booking.first_name, booking.last_name].filter(Boolean).join(" ") || undefined;
  // Every booking in a linked booking falls on the same day, so the primary
  // booking's formatted date is shared with each linked booking card.
  const formattedBookingDate = formatBookingDate(booking.date, venue.country_code ?? "GB", "short");

  return (
    <section id="gc-booking-confirmation" aria-label={t("booking_confirmation_section_label", "Booking confirmation")}>
      {/* Reuse confirmation header from PaymentConfirmationLayout */}
      <BookingConfirmedHeader venueName={venue.title} bookingRef={booking.reference} mode={headerMode} />

      {/* Return URL redirect logic — straight under the header so the guest sees it without scrolling */}
      {isRedirect && returnUrl && returnRedirect && (
        <ReturnUrlRedirect
          returnUrl={returnUrl}
          returnMethod={returnMethod}
          payload={returnRedirect.payload}
          postBooking={returnRedirect.postBooking}
          purchaseTracked={purchaseTracked}
        />
      )}

      {/* Status box summarising the pre-order / payment / authenticate outcome */}
      <BookingActionSummaryCard
        hasPreorder={hasPreorders}
        didPay={didPay}
        didAuthenticate={didAuthenticate}
        amount={paidAmount}
        currency={venue.currency}
        countryCode={venue.country_code ?? "GB"}
      />

      {/* Reuse BookingDetailsCard from PaymentConfirmationLayout, including pre-order details */}
      <BookingDetailsCard
        partySize={booking.num_people}
        date={formattedBookingDate}
        time={formatTime(booking.time, booking.date, venue.country_code ?? "GB", timeLocale)}
        location={venue.title}
        currency={venue.currency}
        countryCode={venue.country_code ?? "GB"}
        customerName={customerName}
        bookingRef={booking.reference}
        bookingType={booking.type.name}
        specialRequest={specialRequest}
        requestedPackages={requestedPackageLabel}
        selectedOffer={selectedOfferTitle}
        preOrderItems={hasPreorders ? preorderSelections?.rawItems : undefined}
        serviceChargeAmount={preorderSelections?.serviceChargeAmount}
        serviceChargePercentage={preorderSelections?.serviceChargePercentage}
        totalCostWithService={preorderSelections?.total}
        taxInclusive={preorderSelections?.taxInclusive}
        taxBreakdown={preorderSelections?.taxBreakdown}
        totalGross={preorderSelections?.totalGross}
        taxLabel={preorderSelections?.taxLabel}
      />

      {/* Each linked booking is a booking in its own right, so it gets its own details card */}
      <LinkedBookingsDetails
        bookings={linkedBookings}
        location={venue.title}
        date={formattedBookingDate}
        currency={venue.currency}
        countryCode={venue.country_code}
        customerName={customerName}
      />

      {/* Rewards banner — shown only when Collins would actually award the points */}
      {rewards && rewardsEligible && (
        <RewardsBanner
          variant={rewardsVariant}
          rewards={rewards}
          currency={venue.currency}
          countryCode={venue.country_code ?? "GB"}
          pointsBalance={signUpBalance ?? signedInBalance ?? pointsBalance}
          rewardsUrl={rewardsUrl}
        />
      )}

      {/* Sign-in section — lets an existing account see its balance and opens
          the rewards link authenticated */}
      {showSignInSection && booking.email && (
        <RewardsSignInSection email={booking.email} onSignInSuccess={handleSignInSuccess} />
      )}

      {/* Sign-up section — the only way a logged-out guest can claim their points */}
      {showSignUpSection && (
        <RewardsSignUpSection
          email={booking.email}
          firstName={booking.first_name}
          lastName={booking.last_name}
          newsletterSignup={newsletterSignup}
          onSignUpSuccess={(pointsBalance) => {
            setSignUpSuccess(true);
            setSignUpBalance(pointsBalance);
          }}
        />
      )}

      {/* Sign-up success message */}
      {signUpSuccess && (
        <AlertBanner className="gcw:mb-bs-3">
          <output>{t("sign_up_success", "Account created successfully!")}</output>
        </AlertBanner>
      )}

      {/* Feature sentiment collector — reused from the payment confirmation flow */}
      <FeatureSentiment featureName="Booking Journey" customerName={customerName} isBookingJourney />
    </section>
  );
};

export default BookingConfirmation;
